import React, { useState } from 'react';
import { FaUpload, FaCheckCircle, FaTimesCircle, FaImage } from 'react-icons/fa';
import { useLanguage } from '../../contexts/LanguageContext';
import WebcamCapture from './WebcamCapture';
import { ACCEPT_IMAGES, checkUploadFile, uploadProblemKey } from "../../utils/uploadPolicy";

export interface RoomDocumentState {
  file: File | null;
  preview: string | null;
  uploaded: boolean;
  uploading: boolean;
  existingUrl?: string;
}

interface ClinicRoomsUploadProps {
  rooms: RoomDocumentState[];
  onRoomsChange: (rooms: RoomDocumentState[]) => void;
  onError: (error: string) => void;
  isReadOnly?: boolean;
  loading?: boolean;
  title?: string;
  required?: boolean;
  maxFileSizeMB?: number;
  maxCount?: number;
  itemLabel?: string;
}

const ClinicRoomsUpload: React.FC<ClinicRoomsUploadProps> = ({
  rooms,
  onRoomsChange,
  onError,
  isReadOnly = false,
  loading = false,
  title,
  required = true,
  maxFileSizeMB = 10,
  maxCount,
  itemLabel,
}) => {
  const { t, isRTL } = useLanguage();

  // Webcam state
  const [webcamOpen, setWebcamOpen] = useState(false);
  const [currentRoomIndex, setCurrentRoomIndex] = useState<number | undefined>(undefined);

  const handleFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
    roomIndex: number
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Same type/size rules as the server (shared/utils/uploadPolicy).
    const problem = checkUploadFile(file, { allowPdf: false });
    if (problem || file.size > maxFileSizeMB * 1024 * 1024) {
      onError(t(uploadProblemKey(problem || "too_large", false)));
      return;
    }

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);

    const newRooms = [...rooms];
    newRooms[roomIndex] = {
      file,
      preview: previewUrl,
      uploaded: false,
      uploading: false,
    };
    onRoomsChange(newRooms);
  };

  const handleWebcamCapture = (file: File) => {
    if (currentRoomIndex === undefined) return;

    const previewUrl = URL.createObjectURL(file);

    const newRooms = [...rooms];
    newRooms[currentRoomIndex] = {
      file,
      preview: previewUrl,
      uploaded: false,
      uploading: false,
    };
    onRoomsChange(newRooms);
  };

  const openWebcam = (roomIndex: number) => {
    setCurrentRoomIndex(roomIndex);
    setWebcamOpen(true);
  };

  const addRoomPhoto = () => {
    onRoomsChange([
      ...rooms,
      {
        file: null,
        preview: null,
        uploaded: false,
        uploading: false,
      },
    ]);
  };

  const removeRoomPhoto = (index: number) => {
    const newRooms = rooms.filter((_, i) => i !== index);
    onRoomsChange(newRooms);
  };

  const RoomUploadCard = ({
    roomIndex,
    state,
    inputId,
  }: {
    roomIndex: number;
    state: RoomDocumentState;
    inputId: string;
  }) => {
    const label = itemLabel || t('room_photo') || 'Room Photo';
    const roomTitle = `${label} ${roomIndex + 1}`;
    const roomDescription = itemLabel
      ? `${label} ${roomIndex + 1}`
      : (t('photo_of_clinic_room')?.replace('{number}', String(roomIndex + 1)) || `Photo of clinic room ${roomIndex + 1}`);

    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {roomTitle}
            </h3>
            <p className="text-sm text-gray-600 mt-1">{roomDescription}</p>
          </div>
        </div>

        {state.preview ? (
          <div className="relative">
            <img
              src={state.preview}
              alt={roomTitle}
              className="w-full h-48 object-cover rounded-lg"
            />
            {state.uploaded && !state.uploading && (
              <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-2">
                <FaCheckCircle size={20} />
              </div>
            )}
            {state.uploading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
              </div>
            )}
            {!isReadOnly && (
              <label className={`mt-3 w-full px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 ${
                state.uploading || loading
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer'
              }`}>
                <FaUpload />
                <span>{t('change_photo') || 'Change Photo'}</span>
                <input
                  type="file"
                  accept={ACCEPT_IMAGES}
                  onChange={(e) => handleFileSelect(e, roomIndex)}
                  className="hidden"
                  disabled={state.uploading || loading}
                />
              </label>
            )}
          </div>
        ) : isReadOnly ? (
          <div className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg border-gray-200 bg-gray-50">
            <FaImage className="w-12 h-12 text-gray-400 mb-3" />
            <p className="text-sm text-gray-600">
              <span className="font-semibold">{t('no_photo_uploaded') || 'No photo uploaded'}</span>
            </p>
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-3 mb-2">
              {/* Take Photo Button (Camera) */}
              <button
                onClick={() => openWebcam(roomIndex)}
                disabled={state.uploading || loading}
                type="button"
                className="flex-1 flex flex-col items-center justify-center h-32 border-2 border-blue-400 bg-white rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="text-4xl mb-2">📷</div>
                <span className="text-sm font-medium text-gray-700">{t('take_photo') || 'Take Photo'}</span>
              </button>

              {/* Or Divider */}
              <span className="text-sm text-gray-500 font-medium">{t('or') || 'Or'}</span>

              {/* Choose File Button */}
              <label
                htmlFor={`${inputId}-file`}
                className="flex-1 flex flex-col items-center justify-center h-32 border-2 border-blue-400 bg-white rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <FaUpload className="text-3xl text-gray-600 mb-2" />
                <span className="text-sm font-medium text-gray-700">{t('choose_from_files') || 'Choose from Files'}</span>
                <input
                  id={`${inputId}-file`}
                  type="file"
                  className="hidden"
                  accept={ACCEPT_IMAGES}
                  onChange={(e) => handleFileSelect(e, roomIndex)}
                  disabled={state.uploading || loading}
                />
              </label>
            </div>

            <p className="text-xs text-center text-gray-400">{t('png_jpg_up_to_10mb') || 'PNG, JPG up to 10MB'}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="bg-gray-50 rounded-xl p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">
          {title || t('clinic_rooms') || 'Clinic Rooms'} {required && <span className="text-red-500">*</span>}
        </h3>

        <div className="space-y-4 mb-4">
          {rooms.map((room, index) => (
            <div key={index} className="relative">
              <RoomUploadCard
                roomIndex={index}
                state={room}
                inputId={`clinic-room-${index}`}
              />
              {!isReadOnly && rooms.length > 1 && (
                <button
                  onClick={() => removeRoomPhoto(index)}
                  disabled={loading || room.uploading}
                  className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors shadow-lg z-10`}
                  title={t('remove_room_photo') || 'Remove room photo'}
                >
                  <FaTimesCircle size={20} />
                </button>
              )}
            </div>
          ))}
        </div>

        {!isReadOnly && (!maxCount || rooms.length < maxCount) && (
          <button
            onClick={addRoomPhoto}
            disabled={loading}
            className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            + {itemLabel || t('add_room_photo') || 'Add Room Photo'}
          </button>
        )}
      </div>

      {/* Webcam Modal */}
      {webcamOpen && (
        <WebcamCapture
          onCapture={handleWebcamCapture}
          onClose={() => setWebcamOpen(false)}
        />
      )}
    </>
  );
};

export default ClinicRoomsUpload;
